import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';

import moment from 'moment';

import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import HeaderMobile from 'components/HeaderMobile';
import useCompanyConfiguration from 'hooks/useCompanyConfiguration';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { AirFareConditions } from 'models/flightValidation';
import { TravelDetails } from 'models/travelDetails';
import { copyContent } from 'utils/clipboard';
import { reservationCompanyImage, reservationImageByDestination } from 'utils/images';

import BookingHistory from '../BookingHistory';
import ExchangeWarning from '../ExchangeWarning';
import Timeline from '../Timeline';
import CancellationPolicies from './components/CancellationPolicies';
import MenuItem from './components/MenuItem';
import PassengerBox from './components/PassengerBox';
import PriceBox from './components/PriceBox';
import SectorDetails from './components/SectorDetails';
import {
  Header,
  HeaderButton,
  Container,
  Destination,
  GoingIn,
  Content,
  Menu,
  AirIcon,
  IconCopyPaste,
  IconExchange,
  IconCancel,
  IconHistory,
  IconHelp,
  Wrapper,
  Section,
} from './styles';
import TravelTag from 'screens/components/TravelRequested/components/TravelTag';

type Props = {
  show: boolean;
  onBack: () => void;
  onClose: () => void;
  onChange?: (airFareConditions?: AirFareConditions[]) => void;
  onCancel?: () => void;
  data: TravelDetails;
  travelId?: string;
};

interface Airlines {
  airlineCode: string;
  airlineName: string;
  airlineLogo: string;
  code: string;
}

const FlightDetailsSection = ({ show, onClose, onBack, onChange, onCancel, data, travelId }: Props) => {
  const capitalizeFirstLetter = (string) => string.charAt(0).toUpperCase() + string.slice(1);
  const { t } = useStoreTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);

  function getAirlines(data: any): Airlines[] {
    if (!data || !Array.isArray(data.flightSectors)) return [];

    const seenCodes = new Set<string>();

    return data.flightSectors
      .map(sector => {
        const flight = sector.flights?.[0];
        if (!flight) return null;

        const code = flight.airlineConfirmationCode;
        if (!code || seenCodes.has(code)) return null;

        seenCodes.add(code);

        return {
          airlineCode: flight.airlineCode,
          airlineName: capitalizeFirstLetter(flight.airlineName),
          code: code,
          airlineLogo: reservationCompanyImage(flight.airlineCode),
        } as Airlines;
      })
      .filter((item): item is Airlines => item !== null);
  }

  const arrivalDate = useMemo(
    () =>
      data.flightSectors && data.flightSectors[0] && data.flightSectors[0].flights
        ? data.flightSectors[0].flights[0].arrivalDate
        : new Date(),
    [data.flightSectors],
  );
  const history = useHistory();
  const { airlineConfirmationCode } = data.requester;
  const arrivalCityName =
    data.flightSectors && data.flightSectors[0] && data.flightSectors[0].flights
      ? data.flightSectors[0].flights.last().arrivalCityName
      : '';
  const arrivalCode =
    data.flightSectors && data.flightSectors[0] && data.flightSectors[0].flights
      ? data.flightSectors[0].flights.last().arrivalAirport
      : undefined;
  const airlineCode =
    data.flightSectors && data.flightSectors[0] && data.flightSectors[0].flights
      ? data.flightSectors[0].flights[0].airlineCode
      : null;

  const airlineCodes = getAirlines(data);

  const airlineLogo = useMemo(() => {
    if (airlineCode) {
      return reservationCompanyImage(airlineCode);
    }
    return null;
  }, [airlineCode]);

  

  const canChangeFlight =
    data.numberOfApprovers >= data.numberOfExpectedApprovers &&
    data.air.airFareConditions[0].changesAllowed &&
    data.advancedPurchase >= 0 &&
    data.travelStatus === 'Confirmed';

  const [showHistory, setShowHistory] = useState(false);
  const handleHelp = () => history.push(`/corporate/customerSupport/trip/${travelId}`);  
  const expirationTime = useMemo(() => capitalizeFirstLetter(moment(arrivalDate).fromNow()), [arrivalDate]);
  const isActive = useMemo(
    () =>
      data.air &&
      data.air.sectorsExchanged &&
      data.air.flightExchangedPending === true &&
      data.air.flightExchangedConfirmed === false
        ? true
        : false,
    [data.air],
  );
  const note = useMemo(() => {
    if (isActive) {
      return t('flightDetails.section.note');
    }
    return undefined;
  }, [isActive, t]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedTab, setSelectedTab] = useState('1');
  const handleChange = useCallback(() => {
    if (
      data.air &&
      data.air?.sectorsExchanged &&
      (data.air.flightExchangedConfirmed || data.air.flightExchangedPending)
    ) {
      setShowConfirm(true);
    } else if (onChange !== undefined) {
      onChange(data.air?.airFareConditions);
    }
  }, [data.air, onChange]);

  const companyConfiguration = useCompanyConfiguration();

  if (showHistory)
    return (
      <BookingHistory
        show={showHistory}
        onClose={() => setShowHistory(false)}
        onBack={() => setShowHistory(false)}
        onDetails={(isExchange: boolean) => {
          setShowHistory(false);
          setTimeout(() => {
            setSelectedTab(isExchange ? '2' : '1');
            if (ref.current && detailRef.current && ref.current.parentElement) {
              const top = detailRef.current.offsetTop + 143;
              ref.current.parentElement.scrollTo({ top, behavior: 'smooth' });
            }
          }, 500);
        }}
        data={data}
      />
    );

  const renderAirlineHeader = ({ airlineCodes, t, copyContent }) => {
    const [showAll, setShowAll] = useState(false);

    return (
      <Menu.Header>
        <Menu.Title>{t('flightDetails.section.airlineCode')}</Menu.Title>
        <Menu.Text>{t('flightDetails.section.airlineText')}</Menu.Text>

        {airlineCodes && airlineCodes.length > 0 &&
          airlineCodes.map((airline, index) => (
            <Menu.Content
              $hide={!showAll && index > 2}
              id={`airlineCode_${index}`}
              key={index}
              onClick={() => copyContent(airline.code)}
            >
              <Menu.Code>
                <Menu.Icon>
                  {airline.airlineLogo && <AirIcon src={airline.airlineLogo} />}
                </Menu.Icon>
                {airline.airlineName}: {airline.code}
              </Menu.Code>
              <Menu.Arrow>
                <IconCopyPaste />
              </Menu.Arrow>
            </Menu.Content>
          ))
        }

        {airlineCodes.length > 3 && (
          <Menu.Text
            onClick={() => setShowAll(prev => !prev)}
            style={{ cursor: 'pointer', marginTop: '8px', textDecoration: 'underline' }}
          >
            {showAll ? t('flightDetails.section.hideAll') : t('flightDetails.section.showAll')}
          </Menu.Text>
        )}
      </Menu.Header>
    );
  }
  const ExchangeMenuItem = () =>
    note ? (
      <MenuItem
        label={t('flightDetails.section.menu.exchange')}
        isActive={isActive}
        menuIcon={<IconExchange />}
        onClick={handleChange}
        hasNote={true}
        note={note}
      />
    ) : (
      <MenuItem
        label={t('flightDetails.section.menu.exchange')}
        isActive={isActive}
        menuIcon={<IconExchange />}
        onClick={handleChange}
      />
    );

  return (
    <>
      <ExchangeWarning
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={() => {
          setShowConfirm(false);
          if (onChange) onChange(data.air?.airFareConditions);
        }}
        isLoading={false}
      />
      <ApplicationOverlayContent width={800} show={show} onClose={onClose} >
        <HeaderMobile
          goBack={onBack}
          onClose={onClose}
          rightButton={
            <HeaderButton>
              <HeaderButton.Button onClick={handleHelp}>
                <HeaderButton.Icon />
              </HeaderButton.Button>
              <HeaderButton.Label>{t('flightDetails.section.help')}</HeaderButton.Label>
            </HeaderButton>
          }
        >
          <Header>
            <Header.Title>
              {t('flightDetails.section.title')} {arrivalCityName}
            </Header.Title>
            <Header.Subtitle>
              {t('flightDetails.section.subtitle', { companyName: companyConfiguration?.companyName })}{' '}
              {data.air?.orderNumber}
            </Header.Subtitle>
          </Header>
        </HeaderMobile>
        <Container ref={ref}>
          <GoingIn>
            <GoingIn.Icon />
            <GoingIn.Text>{expirationTime}</GoingIn.Text>
          </GoingIn>
          {arrivalCode && <Destination $image={reservationImageByDestination(arrivalCode)} />}
          <Wrapper>
            <Content>
              <Content.Header>
                {data.travelStatus === 'Confirmed' ? (
                  <Content.Title>{t('flightDetails.section.headerTitle')}</Content.Title>
                ) : (
                  <TravelTag status={data.travelStatus} />
                )}
                <Content.Image src="/corporate/images/caller.png" width={80} style={{ marginRight: '-10px' }} />
              </Content.Header>
              <Menu>
                {renderAirlineHeader({ airlineCodes, t, copyContent })}                
                <Menu.Group>
                  {onChange && canChangeFlight && <ExchangeMenuItem />}
                  {onCancel && 1 > 2 && (
                    <MenuItem
                      label={t('flightDetails.section.menu.cancel')}
                      menuIcon={<IconCancel />}
                      onClick={() => {
                        onCancel();
                      }}
                    />
                  )}

                  <MenuItem
                    label={t('flightDetails.section.menu.history')}
                    isActive={
                      data.air && data.air.sectorsExchanged && data.air.sectorsExchanged.length > 0 ? true : false
                    }
                    menuIcon={<IconHistory />}
                    onClick={() => setShowHistory(true)}
                  />

                  <MenuItem label={t('flightDetails.section.menu.help')} menuIcon={<IconHelp />} onClick={handleHelp} />
                </Menu.Group>
              </Menu>
            </Content>
            <Content>
              <Content.Header>
                <Content.Title>{t('flightDetails.section.vouchers.title')}</Content.Title>
                <Content.Image src="/corporate/images/vouchers.png" />
              </Content.Header>
              <Menu>
                <Menu.Header>
                  <Menu.Title>{t('flightDetails.section.vouchers.subtitle')}</Menu.Title>
                  <Menu.Code>{data.requester.email}</Menu.Code>
                  <Menu.Text>{t('flightDetails.section.vouchers.text')}</Menu.Text>
                </Menu.Header>
                {/* <Menu.Group>
                  <MenuItem label="Enviar por email" menuIcon={<IconEmail />} onClick={() => console.log('e-mail')} />
                </Menu.Group> */}
              </Menu>
            </Content>
            <Section ref={detailRef}>
              <Section.Title>{t('flightDetails.section.detail.title')}</Section.Title>
              <SectorDetails
                sectors={data.flightSectors ?? []}
                status={data.requester.status}
                onCancel={onCancel}
                exchangedSectors={data.air?.sectorsExchanged ?? []}
                exchangeConfirmed={data.air?.flightExchangedConfirmed}
                exchangePending={data.air?.flightExchangedPending}
                selectedTab={selectedTab}
              />
            </Section>
            {data.air && data.air.airFareConditions && (
              <Section>
                <Section.Title>{t('flightDetails.section.cancellation.title')}</Section.Title>
                <Section.Text>{t('flightDetails.section.cancellation.text')}</Section.Text>
                <CancellationPolicies policies={data.air.airFareConditions} />
              </Section>
            )}
            <Section>
              <Section.Title>{t('flightDetails.section.passengers.title')}</Section.Title>
              <Section.Text>{t('flightDetails.section.passengers.text')}</Section.Text>
              <PassengerBox requester={data.requester} travelers={data?.air?.traveler ?? []} />
            </Section>
            <Section>
              <Section.Title>{t('flightDetails.section.requestHistory.title')}</Section.Title>
              {data.travelLogs.length > 0 && <Timeline data={data} />}
            </Section>
            {data.prices && (
              <Section>
                <Section.Title>{t('flightDetails.section.price.title')}</Section.Title>
                <Section.Text>{t('flightDetails.section.price.text')}</Section.Text>
                <PriceBox price={data.prices} alternativePrice={data.alternativePrices} travelers={data?.air?.traveler?.length ?? 1} hasPrivateFare={data.air.hasPrivateFare} />
              </Section>
            )}
          </Wrapper>
        </Container>
      </ApplicationOverlayContent>
    </>
  );
};
export default FlightDetailsSection;
